import PropTypes from "prop-types"
import React, { useEffect, useMemo } from "react"
import { Controller, useForm } from "react-hook-form"
import { useMutation } from "react-query"
import { useMarina } from "src/contexts/MarinaContext"
import { formatDate } from "src/main/Billing/Items/helpers"

import AlertBanner from "src/components/AlertBanner"
import Form from "src/components/Form"
import Modal from "src/components/Modal"

import { mergeInvoices } from "src/api/Billing/Payments"

import { useToast } from "src/hooks/use_toast"

import { formatCurrencyFromMinorUnits } from "src/utils/UnitConversion"
import { getCurrentMarinaSlug } from "src/utils/url/parsing/marina"

const MergeInvoiceModal = ({
  invoice,
  availableInvoices,
  isOpen,
  onClose,
  onMergeSubmitSuccess,
}) => {
  const { presentmentCurrency } = useMarina()
  const {
    control,
    handleSubmit,
    setValue,
    reset,
    setError,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      sourceInvoiceId: invoice?.id || "",
      destinationInvoiceId: "",
    },
  })
  const marinaSlug = getCurrentMarinaSlug()
  const showToast = useToast()

  const destinationInvoiceId = watch("destinationInvoiceId")
  const selectedDestinationInvoice = useMemo(() => {
    if (!destinationInvoiceId) return null
    return availableInvoices.find(
      (invoice) => invoice.id === destinationInvoiceId
    )
  }, [destinationInvoiceId, availableInvoices])

  useEffect(() => {
    if (invoice?.id) {
      setValue("sourceInvoiceId", invoice.id)
    }
  }, [invoice, setValue])

  const renderInvoice = (invoice) => {
    if (invoice) {
      return (
        <Form.Select.RichOption key={invoice.id} value={invoice.id}>
          <span>
            {formatCurrencyFromMinorUnits({
              amount: invoice.balance,
              currency: presentmentCurrency,
            })}{" "}
            due on {formatDate(invoice.due_date)}
          </span>
        </Form.Select.RichOption>
      )
    } else {
      return (
        <Form.Select.RichOption value="">
          Select a source invoice
        </Form.Select.RichOption>
      )
    }
  }

  const { mutate, isLoading } = useMutation({
    mutationFn: (data) => {
      return mergeInvoices({
        sourceInvoiceId: data.sourceInvoiceId,
        destinationInvoiceId: data.destinationInvoiceId,
        marinaSlug,
      })
    },
    onSuccess: () => {
      onResetModal()
      showToast("Invoice merged successfully", { type: "success" })
      onMergeSubmitSuccess()
    },
    onError: (error) => {
      const errorMessage =
        error.status === 500
          ? "Something went wrong merging the invoice. Please try again or contact support at mayday@dockwa.com"
          : error.message
      setError("root", {
        type: "server",
        message: errorMessage,
      })
    },
  })

  const destinationInvoiceOptions = useMemo(() => {
    if (!availableInvoices || !invoice) return []
    return availableInvoices.filter(
      (inv) => inv.balance !== 0 && inv.id !== invoice.id
    )
  }, [availableInvoices, invoice])

  const onResetModal = () => {
    reset()
    onClose()
  }

  const newBalance = useMemo(() => {
    if (!selectedDestinationInvoice || !invoice) return 0

    return invoice.balance + selectedDestinationInvoice.balance
  }, [invoice, selectedDestinationInvoice])

  return (
    <Modal isOpen={isOpen} onClose={onResetModal}>
      <Modal.Header title="Merge Invoices" />
      <Modal.Body>
        <div className="mb-4 flex flex-col gap-4">
          <p>
            This action will merge the balance of the source invoice into the
            destination invoice. The due date and payment method from the
            destination invoice will be used on the combined amount.
          </p>
          <div className="flex flex-row gap-4">
            <div className="flex w-1/2 flex-col">
              <Controller
                name="sourceInvoiceId"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Form.Select.Custom
                    onChange={onChange}
                    value={value}
                    formLabel="From (Source Invoice)"
                    disabled
                  >
                    {renderInvoice(invoice)}
                  </Form.Select.Custom>
                )}
              />
            </div>

            <div className="flex w-1/2 flex-col">
              <Controller
                name="destinationInvoiceId"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Form.Select.Custom
                    onChange={onChange}
                    value={value}
                    formLabel="To (Destination Invoice)"
                  >
                    <Form.Select.RichOption value="" disabled hideCheck>
                      Select a destination invoice
                    </Form.Select.RichOption>
                    {destinationInvoiceOptions.map(renderInvoice)}
                  </Form.Select.Custom>
                )}
              />
            </div>
          </div>
          {selectedDestinationInvoice && (
            <AlertBanner type="info">
              This will result in a {newBalance >= 0 ? "payment" : "refund"} of{" "}
              {formatCurrencyFromMinorUnits({
                amount: Math.abs(newBalance),
                currency: presentmentCurrency,
              })}{" "}
              due on {formatDate(selectedDestinationInvoice.due_date)}
            </AlertBanner>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer
        confirmBtnText="Merge Invoices"
        confirmBtnType="submit"
        confirmBtnLoading={isLoading}
        disabled={!selectedDestinationInvoice}
        onSubmit={handleSubmit(mutate)}
        onClose={onResetModal}
      />
      {errors.root ? (
        <div className="mt-4">
          <Form.Error>{errors.root.message}</Form.Error>
        </div>
      ) : null}
    </Modal>
  )
}

MergeInvoiceModal.propTypes = {
  invoice: PropTypes.object,
  availableInvoices: PropTypes.array,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onMergeSubmitSuccess: PropTypes.func.isRequired,
}

export default MergeInvoiceModal
